import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, FileDown, Pencil, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { usePdfExport } from '@/hooks/usePdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stages = ['Cutting', 'Forging', 'Machining', 'HT', 'FG'];

export default function OpeningStock() {
  const [stageStocks, setStageStocks] = useState([]);
  const [rmStocks, setRMStocks] = useState([]);
  const [items, setItems] = useState([]);
  const [currentStageStock, setCurrentStageStock] = useState([]);
  const [currentRMStock, setCurrentRMStock] = useState([]);
  const [showStageForm, setShowStageForm] = useState(false);
  const [showRMForm, setShowRMForm] = useState(false);
  const [editStageDialog, setEditStageDialog] = useState(false);
  const [editRMDialog, setEditRMDialog] = useState(false);
  const { exporting, handleExport } = usePdfExport('/export/opening-stock-pdf', 'Opening_Stock');
  const userObj = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = (entry) => userObj.role === 'admin' || entry.created_by === userObj.username;

  const [stageFormData, setStageFormData] = useState({ part_id: '', stage: '', qty: '', date: new Date().toISOString().split('T')[0] });
  const [rmFormData, setRMFormData] = useState({ steel_grade: '', section: '', qty_weight: '', date: new Date().toISOString().split('T')[0] });
  const [editStageData, setEditStageData] = useState({ id: '', part_id: '', stage: '', qty: '', date: '' });
  const [editRMData, setEditRMData] = useState({ id: '', steel_grade: '', section: '', qty_weight: '', date: '' });

  const headers = () => ({});

  const fetchStageStocks = useCallback(async () => {
    try { const r = await axios.get(`${API}/opening-stock/stage`, {}); setStageStocks(r.data); } catch (err) { console.error("Failed to load data:", err); }
  }, []);
  const fetchRMStocks = useCallback(async () => {
    try { const r = await axios.get(`${API}/opening-stock/rm`, {}); setRMStocks(r.data); } catch (err) { console.error("Failed to load data:", err); }
  }, []);
  const fetchItems = useCallback(async () => {
    try { const r = await axios.get(`${API}/masters/items`, {}); setItems(r.data); } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);
  const fetchCurrentStocks = useCallback(async () => {
    try {
      const h = {};
      const [stageRes, rmRes] = await Promise.all([
        axios.get(`${API}/stock/stage`, { /* headers via cookie */ }),
        axios.get(`${API}/stock/raw-material`, { /* headers via cookie */ })
      ]);
      setCurrentStageStock(stageRes.data);
      setCurrentRMStock(rmRes.data);
    } catch (err) { console.error("Failed to load context data:", err); }
  }, []);

  useEffect(() => {
    fetchStageStocks(); fetchRMStocks(); fetchItems(); fetchCurrentStocks();
  }, [fetchStageStocks, fetchRMStocks, fetchItems, fetchCurrentStocks]);

  const handleStageSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.post(`${API}/opening-stock/stage`, {
        ...stageFormData, qty: parseInt(stageFormData.qty),
        date: new Date(stageFormData.date).toISOString(), created_by: user.username
      }, { headers: headers() });
      toast.success('Opening stock added');
      setShowStageForm(false);
      setStageFormData({ part_id: '', stage: '', qty: '', date: new Date().toISOString().split('T')[0] });
      fetchStageStocks(); fetchCurrentStocks();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add'); }
  };

  const handleRMSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.post(`${API}/opening-stock/rm`, {
        ...rmFormData, qty_weight: parseFloat(rmFormData.qty_weight),
        date: new Date(rmFormData.date).toISOString(), created_by: user.username
      }, { headers: headers() });
      toast.success('Opening RM stock added');
      setShowRMForm(false);
      setRMFormData({ steel_grade: '', section: '', qty_weight: '', date: new Date().toISOString().split('T')[0] });
      fetchRMStocks(); fetchCurrentStocks();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add'); }
  };

  const handleEditStage = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.put(`${API}/opening-stock/stage/${editStageData.id}`, {
        part_id: editStageData.part_id, stage: editStageData.stage,
        qty: parseInt(editStageData.qty), date: new Date(editStageData.date).toISOString(),
        created_by: user.username
      }, { headers: headers() });
      toast.success('Opening stock updated');
      setEditStageDialog(false);
      fetchStageStocks(); fetchCurrentStocks();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update'); }
  };

  const handleEditRM = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.put(`${API}/opening-stock/rm/${editRMData.id}`, {
        steel_grade: editRMData.steel_grade, section: editRMData.section,
        qty_weight: parseFloat(editRMData.qty_weight), date: new Date(editRMData.date).toISOString(),
        created_by: user.username
      }, { headers: headers() });
      toast.success('Opening RM stock updated');
      setEditRMDialog(false);
      fetchRMStocks(); fetchCurrentStocks();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update'); }
  };

  const handleDeleteStage = async (stock) => {
    if (!window.confirm('Delete this opening stock?')) return;
    try {
      await axios.delete(`${API}/opening-stock/stage/${stock.id}`, { headers: headers() });
      toast.success('Deleted'); fetchStageStocks(); fetchCurrentStocks();
    } catch (err) { console.error(err); toast.error('Failed to delete'); }
  };

  const handleDeleteRM = async (stock) => {
    if (!window.confirm('Delete this opening RM stock?')) return;
    try {
      await axios.delete(`${API}/opening-stock/rm/${stock.id}`, { headers: headers() });
      toast.success('Deleted'); fetchRMStocks(); fetchCurrentStocks();
    } catch (err) { console.error(err); toast.error('Failed to delete'); }
  };

  const openEditStage = (stock) => {
    setEditStageData({
      id: stock.id, part_id: stock.part_id, stage: stock.stage,
      qty: stock.qty.toString(), date: new Date(stock.date).toISOString().split('T')[0]
    });
    setEditStageDialog(true);
  };

  const openEditRM = (stock) => {
    setEditRMData({
      id: stock.id, steel_grade: stock.steel_grade, section: stock.section,
      qty_weight: stock.qty_weight.toString(), date: new Date(stock.date).toISOString().split('T')[0]
    });
    setEditRMDialog(true);
  };

  return (
    <div className="space-y-6" data-testid="opening-stock-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="opening-stock-title">OPENING STOCK</h1>
          <p className="text-sm text-text-muted mt-2">Manage opening inventory for all stages and raw materials</p>
        </div>
        <Button data-testid="export-pdf-button" onClick={() => handleExport()} disabled={exporting || (stageStocks.length === 0 && rmStocks.length === 0)} className="bg-green-600 text-white hover:bg-green-700 rounded-sm font-medium">
          <FileDown className="w-4 h-4 mr-2" />{exporting ? 'EXPORTING...' : 'EXPORT PDF'}
        </Button>
      </div>

      {/* Carry-forward info */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wide">Carry-Forward Stock</h3>
            <p className="text-xs text-blue-800 mt-1">
              The ERP automatically carries forward closing stock as the opening for the next period. All transactions (receipts, issues, dispatches) are accumulated dynamically.
              Use the manual entry below only when there is a <strong>variance between ERP stock and physical stock</strong> — for example, after a physical stocktake.
            </p>
          </div>
        </div>
      </div>

      {/* Current ERP Stock Summary */}
      {(currentStageStock.length > 0 || currentRMStock.length > 0) && (
        <div className="bg-white border border-border rounded-sm p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-3">Current ERP Stock (Auto Carry-Forward)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {currentStageStock.filter(s => s.qty !== 0).slice(0, 6).map((s, i) => (
              <div key={i} className="bg-gray-50 border rounded-sm p-2 text-center">
                <p className="text-[10px] font-mono text-text-muted truncate">{s.part_id}</p>
                <p className="text-xs font-bold font-mono">{s.qty.toLocaleString()}</p>
                <p className="text-[10px] text-blue-600 font-medium">{s.stage}</p>
              </div>
            ))}
            {currentRMStock.filter(r => r.qty_weight !== 0).slice(0, 6).map((r, i) => (
              <div key={`rm-${i}`} className="bg-gray-50 border rounded-sm p-2 text-center">
                <p className="text-[10px] font-mono text-text-muted truncate">{r.steel_grade}</p>
                <p className="text-xs font-bold font-mono">{r.qty_weight.toLocaleString(undefined, {maximumFractionDigits: 1})}</p>
                <p className="text-[10px] text-amber-600 font-medium">{r.section}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="stage" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="stage">Stage Stock</TabsTrigger>
          <TabsTrigger value="rm">Raw Material</TabsTrigger>
        </TabsList>

        <TabsContent value="stage" className="space-y-6">
          <div className="flex justify-end">
            <Button data-testid="add-stage-opening-button" onClick={() => setShowStageForm(!showStageForm)} className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium">
              <Plus className="w-4 h-4 mr-2" />{showStageForm ? 'CANCEL' : 'ADD STAGE OPENING STOCK'}
            </Button>
          </div>

          {showStageForm && (
            <div className="bg-white border border-border shadow-sm p-6">
              <h2 className="text-2xl font-bold uppercase mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>ADD STAGE OPENING STOCK</h2>
              <form onSubmit={handleStageSubmit} className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
                  <Input type="date" value={stageFormData.date} onChange={(e) => setStageFormData({...stageFormData, date: e.target.value})} required className="mt-1.5 rounded-none" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part</Label>
                  <Select value={stageFormData.part_id} onValueChange={(v) => setStageFormData({...stageFormData, part_id: v})}>
                    <SelectTrigger className="mt-1.5 rounded-none"><SelectValue placeholder="Select part" /></SelectTrigger>
                    <SelectContent>{items.map(i => <SelectItem key={i.part_id} value={i.part_id}>{i.part_id} - {i.part_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Stage</Label>
                  <Select value={stageFormData.stage} onValueChange={(v) => setStageFormData({...stageFormData, stage: v})}>
                    <SelectTrigger className="mt-1.5 rounded-none"><SelectValue placeholder="Select stage" /></SelectTrigger>
                    <SelectContent>{stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity</Label>
                  <Input type="number" value={stageFormData.qty} onChange={(e) => setStageFormData({...stageFormData, qty: e.target.value})} required className="mt-1.5 rounded-none" />
                </div>
                <div className="col-span-4 flex justify-end pt-4">
                  <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">ADD OPENING STOCK</Button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white border border-border shadow-sm">
            {stageStocks.length === 0 ? (
              <div className="p-12 text-center text-text-muted">No opening stage stock entries</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full caption-bottom text-sm" data-testid="stage-opening-table">
                  <thead>
                    <tr className="border-b">
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Stage</th>
                      <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Quantity</th>
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Created By</th>
                      <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stageStocks.map((stock, idx) => (
                      <tr key={idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`stage-opening-row-${idx}`}>
                        <td className="p-4 align-middle font-mono text-xs">{new Date(stock.date).toLocaleDateString()}</td>
                        <td className="p-4 align-middle font-mono text-xs font-semibold">{stock.part_id}</td>
                        <td className="p-4 align-middle">
                          <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-blue-50 text-blue-700">{stock.stage}</span>
                        </td>
                        <td className="p-4 align-middle text-right font-mono text-xs">{stock.qty.toLocaleString()}</td>
                        <td className="p-4 align-middle text-xs text-text-muted">{stock.created_by}</td>
                        <td className="p-4 align-middle">
                          <div className="flex items-center justify-center gap-1">
                            {canModify(stock) && (<>
                            <button data-testid={`edit-stage-opening-${idx}`} onClick={() => openEditStage(stock)} className="p-2 hover:bg-blue-50 hover:text-blue-600 transition-all rounded-sm" title="Edit">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteStage(stock)} className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                            </>)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="rm" className="space-y-6">
          <div className="flex justify-end">
            <Button data-testid="add-rm-opening-button" onClick={() => setShowRMForm(!showRMForm)} className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium">
              <Plus className="w-4 h-4 mr-2" />{showRMForm ? 'CANCEL' : 'ADD RM OPENING STOCK'}
            </Button>
          </div>

          {showRMForm && (
            <div className="bg-white border border-border shadow-sm p-6">
              <h2 className="text-2xl font-bold uppercase mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>ADD RM OPENING STOCK</h2>
              <form onSubmit={handleRMSubmit} className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
                  <Input type="date" value={rmFormData.date} onChange={(e) => setRMFormData({...rmFormData, date: e.target.value})} required className="mt-1.5 rounded-none" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Steel Grade</Label>
                  <Input value={rmFormData.steel_grade} onChange={(e) => setRMFormData({...rmFormData, steel_grade: e.target.value})} required className="mt-1.5 rounded-none" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Section</Label>
                  <Input value={rmFormData.section} onChange={(e) => setRMFormData({...rmFormData, section: e.target.value})} required className="mt-1.5 rounded-none" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Qty (Weight)</Label>
                  <Input type="number" step="0.01" value={rmFormData.qty_weight} onChange={(e) => setRMFormData({...rmFormData, qty_weight: e.target.value})} required className="mt-1.5 rounded-none" />
                </div>
                <div className="col-span-4 flex justify-end pt-4">
                  <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">ADD OPENING STOCK</Button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white border border-border shadow-sm">
            {rmStocks.length === 0 ? (
              <div className="p-12 text-center text-text-muted">No opening RM stock entries</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full caption-bottom text-sm" data-testid="rm-opening-table">
                  <thead>
                    <tr className="border-b">
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Steel Grade</th>
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Section</th>
                      <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Qty Weight</th>
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Created By</th>
                      <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rmStocks.map((stock, idx) => (
                      <tr key={idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`rm-opening-row-${idx}`}>
                        <td className="p-4 align-middle font-mono text-xs">{new Date(stock.date).toLocaleDateString()}</td>
                        <td className="p-4 align-middle font-mono text-xs">{stock.steel_grade}</td>
                        <td className="p-4 align-middle font-mono text-xs">{stock.section}</td>
                        <td className="p-4 align-middle text-right font-mono text-xs">{stock.qty_weight.toFixed(2)}</td>
                        <td className="p-4 align-middle text-xs text-text-muted">{stock.created_by}</td>
                        <td className="p-4 align-middle">
                          <div className="flex items-center justify-center gap-1">
                            {canModify(stock) && (<>
                            <button data-testid={`edit-rm-opening-${idx}`} onClick={() => openEditRM(stock)} className="p-2 hover:bg-blue-50 hover:text-blue-600 transition-all rounded-sm" title="Edit">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteRM(stock)} className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                            </>)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Stage Dialog */}
      <Dialog open={editStageDialog} onOpenChange={setEditStageDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>EDIT OPENING STOCK</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditStage} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
                <Input type="date" value={editStageData.date} onChange={(e) => setEditStageData({...editStageData, date: e.target.value})} required className="mt-1.5 rounded-none" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part</Label>
                <Select value={editStageData.part_id} onValueChange={(v) => setEditStageData({...editStageData, part_id: v})}>
                  <SelectTrigger data-testid="edit-stage-part-select" className="mt-1.5 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{items.map(i => <SelectItem key={i.part_id} value={i.part_id}>{i.part_id} - {i.part_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Stage</Label>
                <Select value={editStageData.stage} onValueChange={(v) => setEditStageData({...editStageData, stage: v})}>
                  <SelectTrigger data-testid="edit-stage-stage-select" className="mt-1.5 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity</Label>
                <Input data-testid="edit-stage-qty" type="number" value={editStageData.qty} onChange={(e) => setEditStageData({...editStageData, qty: e.target.value})} required className="mt-1.5 rounded-none" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" onClick={() => setEditStageDialog(false)} className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-sm">CANCEL</Button>
              <Button data-testid="save-stage-edit" type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">SAVE</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit RM Dialog */}
      <Dialog open={editRMDialog} onOpenChange={setEditRMDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>EDIT RM OPENING STOCK</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditRM} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
                <Input type="date" value={editRMData.date} onChange={(e) => setEditRMData({...editRMData, date: e.target.value})} required className="mt-1.5 rounded-none" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Steel Grade</Label>
                <Input data-testid="edit-rm-grade" value={editRMData.steel_grade} onChange={(e) => setEditRMData({...editRMData, steel_grade: e.target.value})} required className="mt-1.5 rounded-none" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Section</Label>
                <Input data-testid="edit-rm-section" value={editRMData.section} onChange={(e) => setEditRMData({...editRMData, section: e.target.value})} required className="mt-1.5 rounded-none" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Qty (Weight)</Label>
                <Input data-testid="edit-rm-qty" type="number" step="0.01" value={editRMData.qty_weight} onChange={(e) => setEditRMData({...editRMData, qty_weight: e.target.value})} required className="mt-1.5 rounded-none" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" onClick={() => setEditRMDialog(false)} className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-sm">CANCEL</Button>
              <Button data-testid="save-rm-edit" type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">SAVE</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
